import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { ContentBlock, Image } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import {
    WCL_GRID_MAX_WIDTH,
    WCL_GRID_PADDING_LG,
    WCL_GRID_PADDING_SM
} from "@/libraries/design/foundations/grid/src/base";
import {
    RichTextComponent,
    SECTION_DIVIDER_HEIGHTS
} from "@/libraries/design/utils";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

export interface LogoProps {
    src: string;
    alt: string;
    width?: number;
    href: string;
    dataEventProps?: DataEventProps;
}

export type IntegrationBackgroundType = "polygon" | "full";

export type IntegrationBackgroundColor = "neutral.light100" | "neutral.light0";

export interface IntegrationProps {
    eyebrow?: string;
    title?: string;
    titleAs?: "h2" | "h3";
    body?: string | React.ReactElement<typeof RichTextComponent>;
    linkLabel?: string;
    linkHref?: string;
    linkDataEventProps?: DataEventProps;
    logoLinkDataEventProps?: DataEventProps;
    logos: LogoProps[];
    background?: IntegrationBackgroundType;
    backgroundColor?: IntegrationBackgroundColor;
    flipLayout?: boolean;
}

const logoSizes = {
    regular: {
        size: "78px",
        cornerRadius: "18.7px"
    },
    large: {
        size: "100px",
        cornerRadius: "24px"
    }
};

const IntegrationLogo = (
    props: LogoProps & {
        logoSize: "regular" | "large";
        dataEventProps?: DataEventProps;
    }
) => {
    const logoListItemStyles = css({
        "margin": 0,
        "cursor": "pointer",
        "width": logoSizes["regular"].size,
        "height": logoSizes["regular"].size,
        "backgroundColor": wacToken("color.neutral.light0"),
        "border": `1px solid ${wacToken("color.neutral.light300")}`,
        "borderRadius": logoSizes["regular"].cornerRadius,
        [media.above.md]: {
            width: logoSizes[props.logoSize].size,
            height: logoSizes[props.logoSize].size,
            borderRadius: logoSizes[props.logoSize].cornerRadius
        },
        "&:hover": {
            boxShadow: token("elevation.shadow.overlay")
        }
    });

    const logoLinkStyles = css({
        display: "flex",
        height: "100%",
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        padding: token("space.100")
    });

    const logoImageContainerStyles = css({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: props.width ? `${props.width}px` : "100%",
        height: props.width ? "auto" : "100%"
    });

    if (!props.href) {
        return null;
    }

    return (
        <li css={logoListItemStyles}>
            <a
                href={props.href}
                css={logoLinkStyles}
                {...(props.dataEventProps ?? {})}
            >
                <div css={logoImageContainerStyles}>
                    <Image
                        src={props.src}
                        alt={props.alt}
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain"
                        }}
                    />
                </div>
            </a>
        </li>
    );
};

export const Integration = ({
    eyebrow,
    title,
    titleAs = "h3",
    body,
    linkLabel,
    linkHref,
    linkDataEventProps,
    logoLinkDataEventProps,
    logos,
    background = "polygon",
    backgroundColor = "neutral.light100",
    flipLayout = false
}: IntegrationProps) => {
    const numLogos = logos.filter((logo) => logo.href ?? false).length;
    const logoSize = numLogos <= 6 ? "large" : "regular";

    const responsiveSectionDividerHeightsStyles = css({
        "--section-divider-height": `${SECTION_DIVIDER_HEIGHTS.mobile}px`,
        [media.above.sm]: {
            "--section-divider-height": `${SECTION_DIVIDER_HEIGHTS.tablet}px`
        },
        [media.above.md]: {
            "--section-divider-height": `${SECTION_DIVIDER_HEIGHTS.desktop}px`
        }
    });

    const sectionStyles = css({
        width: "100%",
        position: "relative",
        marginBlock: background === "full" ? 0 : token("space.800"),
        background:
            background === "full"
                ? wacToken(`color.${backgroundColor}`)
                : "transparent",
        paddingBlock: "var(--section-divider-height)"
    });

    const polygonStyles = css({
        "background": wacToken(`color.${backgroundColor}`),
        "position": "absolute",
        "width": "100%",
        "height": `calc(100% - 2 * var(--section-divider-height))`,
        [media.above.sm]: {
            width: `calc(${WCL_GRID_MAX_WIDTH}px + (${WCL_GRID_PADDING_LG}px * 2))`, // for when the grid width is stopped at its max with
            maxWidth: `min(calc(100% - ${WCL_GRID_PADDING_LG}px), calc(${WCL_GRID_MAX_WIDTH}px + 80px))` // for when the grid width is less than its max width, and to have background color max width at grid max width + 80px
        },
        "insetInlineStart": "50%",
        "transform": "translateX(-50%)",
        "::before": {
            content: '""',
            position: "absolute",
            insetBlockStart: "calc(-1 * var(--section-divider-height))",
            height: "var(--section-divider-height)",
            width: "100%",
            background: wacToken(`color.${backgroundColor}`),
            clipPath:
                "polygon(0 0, 70% 16%, 70% 66%, 100% 46%, 100% 100.25%, 0 100.25%);"
        },
        "::after": {
            content: '""',
            position: "absolute",
            height: "var(--section-divider-height)",
            insetBlockStart: "100%",
            width: "100%",
            background: wacToken(`color.${backgroundColor}`),
            clipPath:
                "polygon(0 -0.25%, 100% -0.25%, 100% 48%, 40% 100%, 40% 53%, 0 43%)"
        }
    });

    return (
        <section css={[sectionStyles, responsiveSectionDividerHeightsStyles]}>
            {background === "polygon" && <div css={polygonStyles} />}
            <div css={contentContainerStyles}>
                <ul
                    css={[
                        logosContainerStyles,
                        flipLayout && css({ order: 1 }),
                        numLogos === 7 && sevenLogosGridStyles
                    ]}
                >
                    {logos.map((logo, index) => (
                        <IntegrationLogo
                            key={index}
                            {...logo}
                            logoSize={logoSize}
                            dataEventProps={
                                logo.dataEventProps ?? logoLinkDataEventProps
                            }
                        />
                    ))}
                </ul>
                <ContentBlock
                    eyebrow={eyebrow}
                    title={title}
                    titleAs={titleAs}
                    text={body}
                    callToActionProps={
                        linkLabel && linkHref
                            ? {
                                  link: {
                                      text: linkLabel,
                                      href: linkHref,
                                      ...(linkDataEventProps && {
                                          dataEventProps: linkDataEventProps
                                      })
                                  }
                              }
                            : undefined
                    }
                    alignment={{
                        mobile: "center",
                        tablet: "start",
                        desktop: "start"
                    }}
                />
            </div>
        </section>
    );
};

const contentContainerStyles = css({
    "marginInline": "auto",
    "maxWidth": "100%",
    "width": "100%",
    "position": "relative",
    "zIndex": 1,
    "--width-without-gaps": "calc(100% - var(--cl-gap) * 11)", // width of only the 12 columns, subtracting the 11 gaps in 12 column layout
    "paddingInlineStart": `${WCL_GRID_PADDING_SM}px`,
    "paddingInlineEnd": `${WCL_GRID_PADDING_SM}px`,
    [media.only.sm]: {
        paddingInlineStart: `${WCL_GRID_PADDING_LG}px`,
        paddingInlineEnd: `${WCL_GRID_PADDING_LG}px`
    },
    [media.above.sm]: {
        gridTemplateColumns: "1fr 1fr"
    },
    [media.above.md]: {
        "--cl-gap": "32px",
        "paddingInlineStart": `${WCL_GRID_PADDING_LG}px`,
        "paddingInlineEnd": `${WCL_GRID_PADDING_LG}px`,
        "maxWidth": `${WCL_GRID_MAX_WIDTH + WCL_GRID_PADDING_LG * 2}px`
    },
    "display": "grid",
    "boxSizing": "border-box",
    "gap": "var(--cl-gap)",
    "--cl-gap": "24px",
    "height": "100%",
    "alignItems": "center"
});

const logosContainerStyles = css({
    display: "flex",
    flexWrap: "wrap",
    maxWidth: "408px",
    marginInline: "auto",
    listStyleType: "none",
    padding: 0,
    justifyContent: "center",
    alignItems: "center",
    rowGap: token("space.300"),
    columnGap: token("space.400"),
    [media.below.md]: {
        maxWidth: "320px"
    },
    [media.below.sm]: {
        order: 1
    }
});

const sevenLogosGridStyles = css({
    [media.below.md]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "li:first-child": {
            marginInlineStart: "50px"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "li:nth-child(2)": {
            marginInlineEnd: "50px"
        }
    }
});
